import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, BookMarked, Search, Star, X } from "lucide-react";
import {
  api,
  type WiedzaItem,
  type WiedzaTrack,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

function fold(text: string) {
  return (text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l");
}

function matchesQuery(item: WiedzaItem, q: string) {
  if (!q.trim()) return true;
  const hay = fold([item.title, item.summary, item.path, item.category, ...(item.tags || [])].join(" "));
  return fold(q)
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => hay.includes(token));
}

function formatLabel(fmt: string) {
  if (fmt === "md") return "markdown";
  if (fmt === "html") return "html";
  if (fmt === "pdf") return "pdf";
  return fmt;
}

function ProgressBar({ pct, className }: { pct: number; className?: string }) {
  const value = Math.max(0, Math.min(100, pct || 0));
  if (value <= 0) return null;
  return (
    <div className={cn("h-1 w-full overflow-hidden rounded-full bg-muted", className)} title={`${Math.round(value)}%`}>
      <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${value}%` }} />
    </div>
  );
}

function StarButton({
  starred,
  onToggle,
  size = "sm",
}: {
  starred: boolean;
  onToggle: () => void;
  size?: "sm" | "md";
}) {
  return (
    <button
      type="button"
      aria-label={starred ? "Usuń gwiazdkę" : "Dodaj gwiazdkę"}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        "rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-amber-500",
        starred && "text-amber-500",
        size === "md" && "p-1.5",
      )}
    >
      <Star className={cn(size === "md" ? "h-5 w-5" : "h-4 w-4", starred && "fill-current")} />
    </button>
  );
}

function ItemCard({
  item,
  active,
  categoryLabel,
  onOpen,
  onToggleStar,
}: {
  item: WiedzaItem;
  active: boolean;
  categoryLabel: string;
  onOpen: () => void;
  onToggleStar: () => void;
}) {
  return (
    <div
      className={cn(
        "h-full w-full rounded-lg border p-3 text-left transition hover:border-brand/40 hover:bg-muted/40",
        active && "border-brand bg-brand/5",
        !item.available && "opacity-70",
      )}
    >
      <div className="flex items-start gap-1">
        <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-medium leading-snug">{item.title}</p>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{item.summary}</p>
            </div>
            {item.featured ? <Badge variant="secondary">start</Badge> : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge variant="outline">{categoryLabel}</Badge>
            <Badge variant="outline">{formatLabel(item.format)}</Badge>
            {item.minutes ? <Badge variant="outline">~{item.minutes} min</Badge> : null}
            {!item.available ? <Badge variant="destructive">brak pliku</Badge> : null}
            {item.completed ? <Badge variant="secondary">przeczytane</Badge> : null}
            {(item.tags || []).slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>
          <ProgressBar pct={item.scroll_pct || 0} className="mt-2" />
        </button>
        <StarButton starred={Boolean(item.starred)} onToggle={onToggleStar} />
      </div>
    </div>
  );
}

function extractReadableHtml(raw: string): string {
  const article = raw.match(/<article\b[\s\S]*?<\/article>/i);
  if (article) {
    const styleMatch = raw.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    const style = styleMatch ? `<style>${styleMatch[1]}</style>` : "";
    return `${style}${article[0]}`;
  }
  const body = raw.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  if (body) return body[1];
  return raw;
}

function HtmlReader({
  html,
  itemId,
  initialPct,
  onProgress,
  onNavigateItem,
  className,
}: {
  html: string;
  itemId: string;
  initialPct: number;
  onProgress: (pct: number, anchor?: string) => void;
  onNavigateItem: (id: string) => void;
  className?: string;
}) {
  const readable = useMemo(() => extractReadableHtml(html), [html]);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const restoredRef = useRef<string>("");
  const lastSentRef = useRef(0);

  useEffect(() => {
    restoredRef.current = "";
  }, [itemId, readable]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !readable) return;
    if (restoredRef.current === itemId) return;
    restoredRef.current = itemId;
    const target = Math.max(0, Math.min(100, initialPct || 0));
    requestAnimationFrame(() => {
      const max = el.scrollHeight - el.clientHeight;
      if (max > 0 && target > 1) el.scrollTop = (target / 100) * max;
    });
  }, [itemId, readable, initialPct]);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max <= 0 ? 100 : Math.round((el.scrollTop / max) * 1000) / 10;
    const now = Date.now();
    if (now - lastSentRef.current < 1500 && pct < 95) return;
    lastSentRef.current = now;
    onProgress(pct);
  }, [onProgress]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!href) return;
      try {
        const url = new URL(href, window.location.origin);
        const id = url.searchParams.get("id");
        if (id && (url.pathname.includes("/wiedza") || href.startsWith("?id=") || href.startsWith("/wiedza"))) {
          e.preventDefault();
          onNavigateItem(id);
          return;
        }
      } catch {
        /* ignore */
      }
      if (href.startsWith("?id=")) {
        e.preventDefault();
        onNavigateItem(href.slice(4));
      }
    },
    [onNavigateItem],
  );

  return (
    <div
      ref={scrollerRef}
      onScroll={handleScroll}
      onClick={handleClick}
      className={cn("wiedza-html-host h-full overflow-y-auto bg-[#f7f2ee]", className)}
      // HTML pochodzi z allowlistowanego builda Wiedzy (repo), nie z user input.
      dangerouslySetInnerHTML={{ __html: readable }}
    />
  );
}

export default function Wiedza() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("id") || "";
  const isReading = Boolean(selectedId);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [tag, setTag] = useState("");
  const [trackId, setTrackId] = useState("");
  const [starsOnly, setStarsOnly] = useState(false);
  const queryClient = useQueryClient();

  const catalogQuery = useQuery({
    queryKey: ["wiedza-catalog"],
    queryFn: () => api.wiedzaCatalog(),
  });

  const detailQuery = useQuery({
    queryKey: ["wiedza-item", selectedId],
    queryFn: () => api.wiedzaItem(selectedId),
    enabled: Boolean(selectedId),
  });

  const catalog = catalogQuery.data;
  const items = catalog?.items || [];
  const categories = catalog?.categories || [];
  const tracks = catalog?.tracks || [];

  const categoryLabel = useMemo(() => {
    const map = new Map(categories.map((c) => [c.id, c.label]));
    return (id: string) => map.get(id) || id;
  }, [categories]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => (item.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
  }, [items]);

  const activeTrack: WiedzaTrack | undefined = tracks.find((t) => t.id === trackId);

  const filtered = useMemo(() => {
    return items.filter((item) => {
      if (starsOnly && !item.starred) return false;
      if (category && item.category !== category) return false;
      if (tag && !(item.tags || []).includes(tag)) return false;
      if (activeTrack?.tags?.length) {
        const hit = activeTrack.tags.some((t) => (item.tags || []).includes(t));
        if (!hit) return false;
      }
      if (!matchesQuery(item, q)) return false;
      return true;
    });
  }, [items, category, tag, activeTrack, q, starsOnly]);

  const topLevel = useMemo(
    () => filtered.filter((item) => !item.parentId || !filtered.some((p) => p.id === item.parentId)),
    [filtered],
  );

  const childrenOf = (parentId: string) => filtered.filter((item) => item.parentId === parentId);

  const recent = useMemo(() => {
    return [...items]
      .filter((item) => item.last_read_at)
      .sort((a, b) => String(b.last_read_at).localeCompare(String(a.last_read_at)))
      .slice(0, 5);
  }, [items]);

  const selected = items.find((item) => item.id === selectedId) || detailQuery.data?.item;
  const parentOfSelected = selected?.parentId
    ? items.find((item) => item.id === selected.parentId)
    : undefined;
  const lessonChips = selected?.parentId
    ? items.filter((item) => item.parentId === selected.parentId)
    : selectedId
      ? items.filter((item) => item.parentId === selectedId)
      : [];

  const continueItem = useMemo(() => {
    const lastId = catalog?.last_item_id;
    if (!lastId) return undefined;
    return items.find((item) => item.id === lastId);
  }, [catalog?.last_item_id, items]);

  const starMutation = useMutation({
    mutationFn: ({ id, starred }: { id: string; starred: boolean }) => api.wiedzaStar(id, starred),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["wiedza-catalog"] }),
  });

  const progressMutation = useMutation({
    mutationFn: ({ id, scroll_pct }: { id: string; scroll_pct: number }) =>
      api.wiedzaProgress(id, { scroll_pct }),
    onSuccess: (row, vars) => {
      queryClient.setQueryData(["wiedza-catalog"], (prev: typeof catalogQuery.data) => {
        if (!prev) return prev;
        return {
          ...prev,
          last_item_id: vars.id,
          items: prev.items.map((item) =>
            item.id === vars.id
              ? {
                  ...item,
                  scroll_pct: row.scroll_pct,
                  completed: row.completed,
                  last_read_at: row.last_read_at || item.last_read_at,
                }
              : item,
          ),
        };
      });
    },
  });

  const openItem = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params);
      next.set("id", id);
      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const clearSelection = useCallback(() => {
    const next = new URLSearchParams(params);
    next.delete("id");
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    if (!selectedId) return;
    void api.wiedzaLast(selectedId).then(() => {
      queryClient.invalidateQueries({ queryKey: ["wiedza-catalog"] });
    });
  }, [selectedId, queryClient]);

  useEffect(() => {
    if (!isReading) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const tagName = (e.target as HTMLElement | null)?.tagName;
      if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") return;
      clearSelection();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isReading, clearSelection]);

  useEffect(() => {
    if (!isReading) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isReading]);

  function applyTrack(track: WiedzaTrack | null) {
    setTrackId(track?.id || "");
    setTag("");
  }

  function toggleStar(item: WiedzaItem) {
    starMutation.mutate({ id: item.id, starred: !item.starred });
  }

  const rawUrl = selectedId ? api.wiedzaRawUrl(selectedId) : "";
  const htmlBody = selected?.format === "html" ? detailQuery.data?.body : null;
  const useInlineHtml = Boolean(
    htmlBody &&
      (selected?.path?.includes("_wiedza-html/") || htmlBody.includes("wiedza-article")),
  );

  if (isReading) {
    return (
      <div className="fixed inset-x-0 top-14 bottom-0 z-20 flex flex-col bg-background md:left-64 md:top-0">
        <header className="shrink-0 border-b bg-card/95 px-3 py-2.5 backdrop-blur md:px-6">
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={clearSelection} className="shrink-0">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Lista
            </Button>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-snug md:text-base">
                {selected?.title || selectedId}
              </p>
              {selected?.summary ? (
                <p className="hidden truncate text-xs text-muted-foreground sm:block">{selected.summary}</p>
              ) : null}
            </div>
            <div className="hidden items-center gap-1 sm:flex">
              <Button asChild variant="ghost" size="sm">
                <Link to="/plan-tygodnia">Plan</Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/karuzele">Karuzele</Link>
              </Button>
            </div>
            {selected ? (
              <StarButton
                starred={Boolean(selected.starred)}
                onToggle={() => toggleStar(selected)}
                size="md"
              />
            ) : null}
          </div>
          <ProgressBar pct={selected?.scroll_pct || 0} className="mt-2" />
          {parentOfSelected ? (
            <button
              type="button"
              className="mt-2 text-left text-xs text-brand underline"
              onClick={() => openItem(parentOfSelected.id)}
            >
              ← {parentOfSelected.title}
            </button>
          ) : null}
          {lessonChips.length > 0 ? (
            <div className="mt-2 flex gap-1.5 overflow-x-auto pb-0.5">
              {lessonChips.map((child) => (
                <Button
                  key={child.id}
                  type="button"
                  size="sm"
                  variant={selectedId === child.id ? "default" : "outline"}
                  className="shrink-0"
                  onClick={() => openItem(child.id)}
                >
                  {child.title.replace(/^Lekcja\s+/i, "").replace(/^Deck\s+/i, "")}
                </Button>
              ))}
            </div>
          ) : null}
        </header>

        <div className="min-h-0 flex-1 bg-[#f7f2ee]">
          {detailQuery.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Ładuję treść…</p>
          ) : detailQuery.isError ? (
            <p className="p-6 text-sm text-destructive">
              Nie udało się wczytać pliku. Może jeszcze nie ma go w paczce na serwerze.
            </p>
          ) : useInlineHtml ? (
            <HtmlReader
              html={htmlBody || ""}
              itemId={selectedId}
              initialPct={selected?.scroll_pct || 0}
              onProgress={(pct) => progressMutation.mutate({ id: selectedId, scroll_pct: pct })}
              onNavigateItem={openItem}
              className="wiedza-read-article h-full"
            />
          ) : selected?.format === "html" || selected?.format === "pdf" ? (
            <iframe
              title={selected?.title || "Dokument"}
              src={rawUrl}
              className="h-full w-full border-0 bg-white"
              onLoad={() => {
                if (selectedId) progressMutation.mutate({ id: selectedId, scroll_pct: 5 });
              }}
            />
          ) : (
            <div
              className="wiedza-prose h-full overflow-y-auto px-4 py-5 sm:px-6 md:mx-auto md:max-w-3xl"
              onScroll={(e) => {
                const el = e.currentTarget;
                const max = el.scrollHeight - el.clientHeight;
                const pct = max <= 0 ? 100 : Math.round((el.scrollTop / max) * 1000) / 10;
                progressMutation.mutate({ id: selectedId, scroll_pct: pct });
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {detailQuery.data?.body || "_Brak treści._"}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Wiedza"
        subtitle="Kursy, playbooki i ściągi w jednym miejscu. Szukaj albo wejdź ścieżką."
      />

      {continueItem ? (
        <Card className="border-brand/30 bg-brand/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Kontynuuj czytanie
              </p>
              <p className="truncate font-medium">{continueItem.title}</p>
              <ProgressBar pct={continueItem.scroll_pct || 0} className="mt-2 max-w-xs" />
            </div>
            <Button type="button" size="sm" onClick={() => openItem(continueItem.id)}>
              Wznów ({Math.round(continueItem.scroll_pct || 0)}%)
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-2 sm:grid-cols-3">
        {tracks.map((track) => (
          <button
            key={track.id}
            type="button"
            onClick={() => applyTrack(trackId === track.id ? null : track)}
            className={cn(
              "rounded-lg border p-3 text-left transition hover:border-brand/40",
              trackId === track.id && "border-brand bg-brand/5",
            )}
          >
            <p className="flex items-center gap-2 text-sm font-semibold">
              <BookMarked className="h-4 w-4 text-brand" />
              {track.label}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{track.summary}</p>
          </button>
        ))}
      </div>

      {recent.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Ostatnio czytane</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {recent.map((item) => (
              <Button
                key={item.id}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => openItem(item.id)}
              >
                {item.starred ? <Star className="mr-1 h-3.5 w-3.5 fill-current text-amber-500" /> : null}
                {item.title.length > 36 ? `${item.title.slice(0, 34)}…` : item.title}
              </Button>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardContent className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-end">
          <label className="min-w-[12rem] flex-1 space-y-1 text-sm">
            <span className="text-muted-foreground">Szukaj</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-8"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="np. hook, MOFU, Diament…"
              />
            </div>
          </label>
          <label className="space-y-1 text-sm sm:w-48">
            <span className="text-muted-foreground">Kategoria</span>
            <Select
              aria-label="Kategoria"
              value={category}
              onChange={setCategory}
              options={[
                { value: "", label: "wszystkie" },
                ...categories.map((c) => ({ value: c.id, label: c.label })),
              ]}
            />
          </label>
          <label className="space-y-1 text-sm sm:w-44">
            <span className="text-muted-foreground">Tag</span>
            <Select
              aria-label="Tag"
              value={tag}
              onChange={setTag}
              options={[
                { value: "", label: "wszystkie" },
                ...allTags.map((t) => ({ value: t, label: t })),
              ]}
            />
          </label>
          <Button
            type="button"
            variant={starsOnly ? "default" : "outline"}
            onClick={() => setStarsOnly((v) => !v)}
          >
            <Star className={cn("mr-1 h-4 w-4", starsOnly && "fill-current")} />
            Gwiazdki
          </Button>
          {(q || category || tag || trackId || starsOnly) && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setQ("");
                setCategory("");
                setTag("");
                setTrackId("");
                setStarsOnly(false);
              }}
            >
              <X className="mr-1 h-4 w-4" />
              Wyczyść
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Lista ({topLevel.length}
            {filtered.length !== items.length ? ` z ${items.length}` : ""})
          </CardTitle>
          {catalogQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Ładuję katalog…</p>
          ) : catalogQuery.isError ? (
            <p className="text-sm text-destructive">Nie udało się wczytać katalogu.</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Dostępne pliki: {catalog?.available_count ?? 0}/{catalog?.count ?? 0}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {topLevel.map((item) => (
              <div key={item.id} className="space-y-1.5">
                <ItemCard
                  item={item}
                  active={false}
                  categoryLabel={categoryLabel(item.category)}
                  onOpen={() => openItem(item.id)}
                  onToggleStar={() => toggleStar(item)}
                />
                {childrenOf(item.id).map((child) => (
                  <div key={child.id} className="pl-2">
                    <ItemCard
                      item={child}
                      active={false}
                      categoryLabel={categoryLabel(child.category)}
                      onOpen={() => openItem(child.id)}
                      onToggleStar={() => toggleStar(child)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
          {!catalogQuery.isLoading && topLevel.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nic nie pasuje do filtrów.</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
